import React, { useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Boply Logo Component
 */
const BoplyLogo = ({ size = 64 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: 0, 
        left: 0,
        opacity: 0.5 
      }}
    />
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.22, 
        left: size * 0.22,
        opacity: 0.75 
      }}
    />
    <div 
      className="absolute bg-blue-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.45, 
        left: size * 0.45 
      }}
    />
  </div>
);

/**
 * View shown when user has no groups
 * 
 * - "Create a New Band" triggers the CreateBandModal (via onCreateBand prop)
 * - "Join Existing Band" shows inline join form
 */
const NoGroupsView = ({ user, onLogout, onCreateBand, joinGroup }) => {
  const [mode, setMode] = useState(null); // null | 'join'
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setLoading(true);
    setError('');
    try {
      await joinGroup(inviteCode.trim().toUpperCase());
      toast.success('Successfully joined the band!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BoplyLogo size={56} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">Boply</h1>
          <p className="text-gray-600 dark:text-gray-400">Welcome! Let's get you set up.</p>
        </div>

        {!mode ? (
          <div className="space-y-4">
            <button
              onClick={onCreateBand}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-500 hover:to-indigo-500 transition-all shadow-lg shadow-purple-500/25"
            >
              Create a New Band
            </button>
            <button
              onClick={() => setMode('join')}
              className="w-full py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Join Existing Band
            </button>
          </div>
        ) : (
          <form onSubmit={handleJoin}>
            <button
              type="button"
              onClick={() => { setMode(null); setError(''); }}
              className="text-gray-600 dark:text-gray-400 text-sm mb-4 hover:underline"
            >
              ← Back
            </button>

            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Join a Band
            </h2>

            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="INVITE CODE"
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500 mb-4 text-center text-2xl font-bold tracking-widest"
              maxLength={8}
              autoFocus
            />

            {error && <p className="text-red-600 dark:text-red-400 text-sm mb-4">{error}</p>}

            <button
              type="submit"
              disabled={loading || !inviteCode.trim()}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Joining...' : 'Join Band'}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Logged in as {user.email}
          </p>
          <button
            onClick={onLogout}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoGroupsView;